import { Container, Group, Title, Text, SimpleGrid, Card, Table, Badge, Skeleton } from '@mantine/core';
import { useBuyAmericaCompliance, useBuyAmericaWaivers } from '@/hooks/usePortalApi';

const STATUS_COLORS: Record<string, string> = {
  approved: 'green',
  pending: 'yellow',
  denied: 'red',
  none: 'gray',
};

export default function BuyAmerica() {
  const { data: compliance, isLoading: complianceLoading } = useBuyAmericaCompliance();
  const { data: waivers, isLoading: waiversLoading } = useBuyAmericaWaivers();

  const fmt = (n: number) => n.toFixed(1) + '%';

  return (
    <Container size="lg" py="md">
      <Group justify="space-between" mb="md">
        <div>
          <Title order={2}>Buy America Compliance</Title>
          <Text c="dimmed" size="sm">Track domestic sourcing requirements</Text>
        </div>
      </Group>

      {complianceLoading ? (
        <SimpleGrid cols={3} mb="xl">
          {[1, 2, 3].map((i) => (
            <Card key={i} shadow="sm" padding="lg" withBorder>
              <Skeleton height={20} width="60%" mb="xs" />
              <Skeleton height={30} width="40%" />
            </Card>
          ))}
        </SimpleGrid>
      ) : compliance ? (
        <SimpleGrid cols={3} mb="xl">
          <Card shadow="sm" padding="lg" withBorder>
            <Text size="sm" c="dimmed" mb="xs">Compliant Products</Text>
            <Text size="xl" fw={700} c="green">{fmt(compliance.compliant_percentage)}</Text>
          </Card>

          <Card shadow="sm" padding="lg" withBorder>
            <Text size="sm" c="dimmed" mb="xs">Non-Compliant Items</Text>
            <Text size="xl" fw={700} c="red">{compliance.non_compliant_count}</Text>
          </Card>

          <Card shadow="sm" padding="lg" withBorder>
            <Text size="sm" c="dimmed" mb="xs">Pending Waivers</Text>
            <Text size="xl" fw={700} c="yellow">{compliance.pending_waivers}</Text>
          </Card>
        </SimpleGrid>
      ) : null}

      <Card shadow="sm" padding="lg" withBorder mb="xl">
        <Title order={4} mb="md">Product Compliance Status</Title>
        {complianceLoading ? (
          <div>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} height={40} mb="sm" />
            ))}
          </div>
        ) : !compliance?.products || compliance.products.length === 0 ? (
          <Text ta="center" c="dimmed" py="md">No compliance data available</Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product Name</Table.Th>
                <Table.Th>Origin Country</Table.Th>
                <Table.Th>Compliant</Table.Th>
                <Table.Th>Waiver Status</Table.Th>
                <Table.Th>Category</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {compliance.products.map((product: any) => (
                <Table.Tr key={product.id}>
                  <Table.Td>{product.name}</Table.Td>
                  <Table.Td>{product.origin_country}</Table.Td>
                  <Table.Td>
                    <Badge color={product.compliant ? 'green' : 'red'} variant="light">
                      {product.compliant ? 'Yes' : 'No'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={STATUS_COLORS[product.waiver_status] || 'gray'} variant="light">
                      {product.waiver_status || 'None'}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{product.category}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Card shadow="sm" padding="lg" withBorder>
        <Title order={4} mb="md">Waiver Requests</Title>
        {waiversLoading ? (
          <div>
            {[1, 2].map((i) => (
              <Skeleton key={i} height={40} mb="sm" />
            ))}
          </div>
        ) : !waivers || waivers.length === 0 ? (
          <Text ta="center" c="dimmed" py="md">No waiver requests</Text>
        ) : (
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Product</Table.Th>
                <Table.Th>Reason</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Date Submitted</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {waivers.map((waiver: any) => (
                <Table.Tr key={waiver.id}>
                  <Table.Td>{waiver.product_name}</Table.Td>
                  <Table.Td>{waiver.reason}</Table.Td>
                  <Table.Td>
                    <Badge color={STATUS_COLORS[waiver.status]} variant="light">
                      {waiver.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{new Date(waiver.submitted_at).toLocaleDateString()}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>
    </Container>
  );
}
